import React, { Fragment, useMemo } from 'react';

export const HASHTAG_NAVIGATION_EVENT = 'mapbl:open-hashtag';

export const requestHashtagNavigation = (hashtag: string) => {
  window.dispatchEvent(new CustomEvent(HASHTAG_NAVIGATION_EVENT, {
    detail: { hashtag },
  }));
};

interface HashtagTextProps {
  text: string;
  hashtagClassName?: string;
  onHashtagClick?: (hashtag: string) => void;
}

interface TextPart {
  text: string;
  hashtag?: string;
}

export const splitHashtagText = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  const pattern = /(^|[^\p{L}\p{N}_])#([\p{L}\p{N}_]{1,100})/gu;
  const normalizedText = text.normalize('NFC');
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(normalizedText)) !== null) {
    const index = match.index ?? 0;
    const prefix = match[1];
    const hashtag = match[2];
    const hashtagStart = index + prefix.length;
    if (hashtagStart > cursor) parts.push({ text: normalizedText.slice(cursor, hashtagStart) });
    parts.push({ text: `#${hashtag}`, hashtag });
    cursor = hashtagStart + hashtag.length + 1;
  }
  if (cursor < normalizedText.length) parts.push({ text: normalizedText.slice(cursor) });
  return parts.length ? parts : [{ text: normalizedText }];
};

const HashtagText: React.FC<HashtagTextProps> = ({
  text,
  hashtagClassName = 'font-semibold text-cyan-600 underline decoration-cyan-400/35 underline-offset-2 transition hover:text-violet-600 dark:text-cyan-300 dark:hover:text-violet-300',
  onHashtagClick,
}) => {
  const parts = useMemo(() => splitHashtagText(text), [text]);

  const openHashtag = (hashtag: string) => {
    if (onHashtagClick) {
      onHashtagClick(hashtag);
      return;
    }
    requestHashtagNavigation(hashtag);
  };

  return (
    <>
      {parts.map((part, index) => part.hashtag ? (
        <button
          key={`${part.hashtag}:${index}`}
          type="button"
          className={hashtagClassName}
          onClick={(event) => {
            event.stopPropagation();
            openHashtag(part.hashtag!);
          }}
          aria-label={`Explore hashtag ${part.hashtag}`}
        >
          {part.text}
        </button>
      ) : <Fragment key={index}>{part.text}</Fragment>)}
    </>
  );
};

export default HashtagText;
